'use client';

import { useState, useEffect } from 'react';
import {
  KioskPage,
  AuthPage,
  AdminPage,
  ViewMode,
  KioskData,
  AdminProfile,
} from './types';
import { CURRENT_ADMIN, getDashboardStats } from './data/mock-data';

// Kiosk Pages
import ShipperInputPage from './components/kiosk/ShipperInputPage';
import PalletQuantityPage from './components/kiosk/PalletQuantityPage';
import VehicleInfoPage from './components/kiosk/VehicleInfoPage';
import PaymentPage from './components/kiosk/PaymentPage';
import ReceiptPage from './components/kiosk/ReceiptPage';

// Auth Pages
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';

// Admin Pages
import DashboardPage from './components/admin/DashboardPage';
import ReceiptsPage from './components/admin/ReceiptsPage';
import ClientsPage from './components/admin/ClientsPage';
import UsersPage from './components/admin/UsersPage';
import SettingsPage from './components/admin/SettingsPage';

// Virtual Keyboard (for kiosk touchscreen)
import VirtualKeyboardProvider from './components/shared/VirtualKeyboardProvider';

const INITIAL_KIOSK_DATA: KioskData = {
  shipperName: '',
  palletQuantity: 0,
  selectedUnit: '1대분',
  vehicleCount: 1,
  vehicleNumber: '',
  contact: '',
  driverName: '',
  paymentMethod: '',
};

export default function PalletKioskDemo() {
  // 뷰 모드: kiosk | admin
  const [viewMode, setViewMode] = useState<ViewMode>('kiosk');

  // 키오스크 페이지
  const [kioskPage, setKioskPage] = useState<KioskPage>('shipper');

  // 인증 페이지
  const [authPage, setAuthPage] = useState<AuthPage>('login');

  // 관리자 페이지
  const [adminPage, setAdminPage] = useState<AdminPage>('dashboard');

  // 로그인 상태
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 키오스크 데이터
  const [kioskData, setKioskData] = useState<KioskData>(INITIAL_KIOSK_DATA);

  // 관리자 프로필
  const [adminProfile] = useState<AdminProfile>({
    user_id: CURRENT_ADMIN.user_id,
    full_name: CURRENT_ADMIN.full_name,
    role: CURRENT_ADMIN.role,
    companies: CURRENT_ADMIN.companies,
  });

  // localStorage에서 데이터 로드
  useEffect(() => {
    const savedData = localStorage.getItem('palletKioskDemoData');
    if (savedData) {
      try {
        // localStorage 는 클라이언트 전용 → SSR 하이드레이션 불일치를 피하려면 mount 후 반영해야 한다
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setKioskData(JSON.parse(savedData));
      } catch (e) {
        console.error('Failed to parse saved data');
      }
    }
  }, []);

  // localStorage에 데이터 저장
  useEffect(() => {
    localStorage.setItem('palletKioskDemoData', JSON.stringify(kioskData));
  }, [kioskData]);

  // 키오스크 데이터 업데이트 함수
  const updateKioskData = (key: keyof KioskData, value: string | number) => {
    setKioskData((prev) => ({ ...prev, [key]: value }));
  };

  // 키오스크 완료 (초기화)
  const handleKioskComplete = () => {
    setKioskData(INITIAL_KIOSK_DATA);
    localStorage.removeItem('palletKioskDemoData');
    setKioskPage('shipper');
  };

  // 관리자 화면으로 이동
  const handleGoToAdmin = () => {
    if (isLoggedIn) {
      setViewMode('admin');
      setAdminPage('dashboard');
    } else {
      setViewMode('admin');
      setAuthPage('login');
    }
  };

  // 키오스크 화면으로 이동
  const handleGoToKiosk = () => {
    setViewMode('kiosk');
  };

  // 로그인 처리
  const handleLogin = () => {
    setIsLoggedIn(true);
    setAdminPage('dashboard');
  };

  // 로그아웃 처리
  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthPage('login');
  };

  // 키오스크 페이지 렌더링
  const renderKioskPage = () => {
    switch (kioskPage) {
      case 'shipper':
        return (
          <ShipperInputPage
            initialValue={kioskData.shipperName}
            onChange={(value) => updateKioskData('shipperName', value)}
            onNext={() => setKioskPage('quantity')}
          />
        );

      case 'quantity':
        return (
          <PalletQuantityPage
            shipperName={kioskData.shipperName}
            initialQuantity={kioskData.palletQuantity ? kioskData.palletQuantity.toString() : ''}
            initialUnit={kioskData.selectedUnit}
            onQuantityChange={(value) => updateKioskData('palletQuantity', parseInt(value) || 0)}
            onUnitChange={(value) => {
              updateKioskData('selectedUnit', value);
              updateKioskData('vehicleCount', parseInt(value.replace('대분', '')) || 1);
            }}
            onPrevious={() => setKioskPage('shipper')}
            onNext={() => setKioskPage('vehicle')}
          />
        );

      case 'vehicle':
        return (
          <VehicleInfoPage
            initialVehicleNumber={kioskData.vehicleNumber}
            initialContact={kioskData.contact}
            initialDriverName={kioskData.driverName}
            onVehicleNumberChange={(value) => updateKioskData('vehicleNumber', value)}
            onContactChange={(value) => updateKioskData('contact', value)}
            onDriverNameChange={(value) => updateKioskData('driverName', value)}
            onPrevious={() => setKioskPage('quantity')}
            onNext={() => setKioskPage('payment')}
          />
        );

      case 'payment':
        return (
          <PaymentPage
            palletQuantity={kioskData.palletQuantity}
            initialPayment={kioskData.paymentMethod}
            onPaymentChange={(value) => updateKioskData('paymentMethod', value)}
            onPrevious={() => setKioskPage('vehicle')}
            onNext={() => setKioskPage('receipt')}
          />
        );

      case 'receipt':
        return (
          <ReceiptPage
            data={kioskData}
            onPrevious={() => setKioskPage('payment')}
            onComplete={handleKioskComplete}
          />
        );
    }
  };

  // 인증 페이지 렌더링
  const renderAuthPage = () => {
    switch (authPage) {
      case 'login':
        return (
          <LoginPage
            onLogin={handleLogin}
            onGoToSignup={() => setAuthPage('signup')}
            onGoToKiosk={handleGoToKiosk}
          />
        );

      case 'signup':
        return (
          <SignupPage
            onSignup={() => setAuthPage('login')}
            onGoToLogin={() => setAuthPage('login')}
          />
        );
    }
  };

  // 관리자 페이지 렌더링
  const renderAdminPage = () => {
    switch (adminPage) {
      case 'dashboard':
        return (
          <DashboardPage
            profile={adminProfile}
            stats={getDashboardStats()}
            onNavigate={setAdminPage}
            onLogout={handleLogout}
            onGoToKiosk={handleGoToKiosk}
            onViewAllReceipts={() => setAdminPage('receipts')}
          />
        );

      case 'receipts':
        return (
          <ReceiptsPage
            profile={adminProfile}
            onNavigate={setAdminPage}
            onLogout={handleLogout}
            onGoToKiosk={handleGoToKiosk}
          />
        );

      case 'clients':
        return (
          <ClientsPage
            profile={adminProfile}
            onNavigate={setAdminPage}
            onLogout={handleLogout}
            onGoToKiosk={handleGoToKiosk}
          />
        );

      case 'users':
        return (
          <UsersPage
            profile={adminProfile}
            onNavigate={setAdminPage}
            onLogout={handleLogout}
            onGoToKiosk={handleGoToKiosk}
          />
        );

      case 'settings':
        return (
          <SettingsPage
            profile={adminProfile}
            onNavigate={setAdminPage}
            onLogout={handleLogout}
            onGoToKiosk={handleGoToKiosk}
          />
        );
    }
  };

  return (
    <VirtualKeyboardProvider>
      <div className="relative">
        {/* 키오스크 모드 - 16:9 비율 컨테이너 */}
        {viewMode === 'kiosk' && (
          <div className="w-full aspect-video overflow-hidden relative">
            {renderKioskPage()}
            {/* 관리자 화면 진입 버튼 */}
            <button
              onClick={handleGoToAdmin}
              className="absolute top-4 left-4 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm shadow-lg hover:bg-gray-700 transition-colors z-50 animate-blink"
            >
              관리자 화면
            </button>
            <style jsx>{`
              @keyframes blink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.2; }
              }
              .animate-blink {
                animation: blink 2s ease-in-out infinite;
              }
            `}</style>
          </div>
        )}

        {/* 관리자 모드 - 로그인 전 */}
        {viewMode === 'admin' && !isLoggedIn && renderAuthPage()}

        {/* 관리자 모드 - 로그인 후 */}
        {viewMode === 'admin' && isLoggedIn && renderAdminPage()}
      </div>
    </VirtualKeyboardProvider>
  );
}
